import type { ReactElement } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { listAlerts } from '../lib/alerts-api.ts'
import { registryKinds } from '../lib/registry-api.ts'
import { Chip, PageHeader } from '../components/ui/index.ts'
import { registryKindLabel } from '../components/RegistryKindNav.tsx'

export const Route = createFileRoute('/')({
  // Best-effort nav badge: any failure (network, 401 on an expired/missing
  // session — including SSR loads, which carry no cookie) just means no
  // count, never a redirect. This is the landing page; it must always
  // render, unlike /dashboard which gates on a valid session. That is why
  // this loader is the one that does NOT use `withSessionRedirect` — the
  // swallow is the point, not an oversight.
  loader: async () => {
    try {
      const openAlerts = await listAlerts('open')
      return { openAlertCount: openAlerts.length }
    } catch {
      return { openAlertCount: null }
    }
  },
  component: Home,
})

const LINK_CLASSES =
  'rounded-sm text-sm text-accent transition-colors hover:underline'

function Home(): ReactElement {
  const { openAlertCount } = Route.useLoaderData()
  const hasOpenAlerts = openAlertCount !== null && openAlertCount > 0

  return (
    <div className="p-8">
      <PageHeader
        // The sidebar wordmark, the header crumb for `/` and the document
        // title all say "Stoaria Control"; this `<h1>` said
        // "stoaria-control admin" — the npm package name — which read as a
        // different product on the one page a new operator lands on.
        title="Stoaria Control"
        description={
          <>
            New here? The mental model in one line: an org{' '}
            <strong>hierarchy</strong> (one root, inherited config) holds
            projects; <strong>harnesses</strong> are enrolled machines that run
            sessions; <strong>CLI profiles</strong> say how to launch an agent;{' '}
            <strong>failover configs</strong> pick profile + machine; a{' '}
            <strong>session</strong> ties it all together. Full walkthrough:{' '}
            <a
              className={LINK_CLASSES}
              href="https://github.com/dromos-inc/stoaria-control/blob/dev/docs/operator-guide.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              operator guide
            </a>
            .
          </>
        }
      />

      {/* The sidebar is the app's navigation now, so this page is not a
          second copy of it — it is the landing page's shortcut list, and
          the two things it has that the sidebar does not are the open-alert
          count and the full registry kind list.

          That list is derived from `registryKinds` and labelled with the
          registry page's own `registryKindLabel`, not a local copy of it:
          the copy that used to live here de-slugged mechanically, so this
          page offered "Cli profiles" and "Mcp definitions" while the page
          it linked to said "CLI profiles". */}
      <section className="grid gap-6 sm:grid-cols-2">
        <div>
          <h2 className="mb-2 text-xs font-medium tracking-wide text-muted uppercase">
            Start here
          </h2>
          <ul className="flex flex-col gap-1.5">
            <li>
              <Link
                to="/dashboard"
                className={`${LINK_CLASSES} inline-flex items-center gap-1.5 font-medium`}
              >
                Dashboard
                {hasOpenAlerts && (
                  // `warn`, not `danger`: an open alert is a thing a human
                  // has to look at, not a failure of the console itself.
                  // The number is the chip's label, so the count is never
                  // carried by the colour alone.
                  <Chip tone="warn" title="Open alerts">
                    {openAlertCount}
                  </Chip>
                )}
              </Link>
            </li>
            <li>
              <Link to="/sessions" className={LINK_CLASSES}>
                Sessions
              </Link>
            </li>
            <li>
              <Link to="/alerts" className={LINK_CLASSES}>
                Alerts
              </Link>
            </li>
            <li>
              <Link to="/hierarchy" className={LINK_CLASSES}>
                Hierarchy
              </Link>
            </li>
            <li>
              <Link to="/memory" className={LINK_CLASSES}>
                Memory
              </Link>
            </li>
            <li>
              <Link to="/login" className={LINK_CLASSES}>
                Sign in
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h2 className="mb-2 text-xs font-medium tracking-wide text-muted uppercase">
            Registry
          </h2>
          <ul className="flex flex-col gap-1.5">
            {registryKinds.map((kind) => (
              <li key={kind}>
                <Link
                  to="/registry/$kind"
                  params={{ kind }}
                  className={LINK_CLASSES}
                >
                  {registryKindLabel(kind)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  )
}
